import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaDownload, FaTrash, FaEdit, FaEnvelope, FaBan, FaCheck, FaPlus, FaSort, FaSearch, FaEye, FaTimes, FaMapMarkerAlt, FaRoute, FaInfoCircle, FaFileAlt, FaCreditCard, FaStar, FaCogs, FaTasks, FaMoneyBillWave, FaBriefcase } from 'react-icons/fa';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import ConfirmModal from '../components/ConfirmModal';
import MessageModal from '../components/MessageModal';
import { usePrestataire, usePrestataireEmployees, usePrestataireServices, usePrestataireDocuments, usePrestataireMissions, usePrestatairePaiements, usePrestataireEvaluations, usePrestataireAbonnement } from '../hooks/usePrestataires';
import { useServiceCategories } from '../hooks/useServices';
import { useZones } from '../hooks/useZones';
import { API_SERVER_URL, getStaticUrl } from '../config/api';
import '../styles/PrestataireDetail.css';

const PrestataireDetail = () => {
  console.log('🚀 PrestataireDetail - Composant chargé !');
  const { id } = useParams();
  const navigate = useNavigate();
  
  console.log('🔍 PrestataireDetail - ID reçu:', id);
  
  const { prestataire: rawPrestataire, loading, error } = usePrestataire(id);
  const { employees, loading: employeesLoading, addEmployee, updateEmployee, deleteEmployee } = usePrestataireEmployees(id);
  const { services, loading: servicesLoading, addService, updateService, deleteService } = usePrestataireServices(id);
  const { documents, loading: documentsLoading, error: documentsError } = usePrestataireDocuments(id);
  const { missions, loading: missionsLoading } = usePrestataireMissions(id);
  const { paiements, stats: paiementStats, loading: paiementsLoading } = usePrestatairePaiements(id);
  const { evaluations, averageRating, loading: evaluationsLoading } = usePrestataireEvaluations(id);
  const { plans, abonnement: currentAbonnement, loading: abonnementLoading } = usePrestataireAbonnement(id);
  const { categories: serviceCategories } = useServiceCategories();
  const { zones: availableZones } = useZones();

  console.log('🔍 PrestataireDetail - Hook result:', { rawPrestataire, loading, error });
  
  // Adapter les données API vers le format attendu par l'interface
  const prestataire = rawPrestataire ? {
    ...rawPrestataire,
    name: rawPrestataire.business_name || `${rawPrestataire.first_name} ${rawPrestataire.last_name}`,
    type: rawPrestataire.profile_type === 'moral' ? 'Entreprise' : 'Particulier',
    date: rawPrestataire.created_at,
    missions: rawPrestataire.statistics?.totalMissions || 0,
    reports: rawPrestataire.statistics?.totalReports || 0,
    status: rawPrestataire.is_active ? 'Actif' : 'Suspendu',
    avatar: rawPrestataire.business_name ? 
      rawPrestataire.business_name.charAt(0).toUpperCase() : 
      `${(rawPrestataire.first_name || '').charAt(0)}${(rawPrestataire.last_name || '').charAt(0)}`.toUpperCase(),
    avatarColor: '#3B82F6',
    address: rawPrestataire.address || 'Adresse non renseignée',
    joinDate: rawPrestataire.created_at,
    prestataireId: rawPrestataire.id ? rawPrestataire.id.substring(0, 8) : 'N/A'
  } : null;
  
  // États pour les modales et formulaires
  const [activeTab, setActiveTab] = useState('information');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [employeeForm, setEmployeeForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: ''
  });

  // États pour ajout service (workflow multi-étapes)
  const [serviceStep, setServiceStep] = useState(1);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    type: '',
    pricing: 'Prix fixe',
    coverage: '',
    zones: [],
    vehicleType: '',
    seats: '',
    registration: '',
    driverName: '',
    driverFirstName: '',
    driverGender: '',
    driverRegistration: '',
    permits: []
  });

  if (loading) {
    return (
      <div className="prestataire-detail-container">
        <div className="loading">
          <h2>Chargement du prestataire...</h2>
        </div>
      </div>
    );
  }

  if (error || !prestataire) {
    return (
      <div className="prestataire-detail-container">
        <div className="not-found">
          <h2>Prestataire non trouvé</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/prestataires')} className="btn btn-primary">
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  // Gestion des employés
  const handleEmployeeFormChange = (value, field) => {
    setEmployeeForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setEmployeeForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: ''
    });
    setShowEmployeeModal(true);
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({ ...employee });
    setShowEmployeeModal(true);
  };

  const handleSaveEmployee = async () => {
    try {
      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, employeeForm);
      } else {
        await addEmployee(employeeForm);
      }
      setShowEmployeeModal(false);
      setEmployeeForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: ''
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'employé:', error);
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Supprimer employé',
      message: 'Êtes-vous sûr de vouloir supprimer cet employé ?',
      onConfirm: async () => {
        try {
          await deleteEmployee(employeeId);
          setConfirmModal({ isOpen: false });
        } catch (error) {
          console.error('Erreur lors de la suppression de l\'employé:', error);
        }
      },
      onCancel: () => setConfirmModal({ isOpen: false })
    });
  };

  // Gestion des services
  const handleAddService = () => {
    setServiceStep(1);
    setServiceForm({
      name: '',
      type: '',
      pricing: 'Prix fixe',
      coverage: '',
      zones: [],
      vehicleType: '',
      seats: '',
      registration: '',
      driverName: '',
      driverFirstName: '',
      driverGender: '',
      driverRegistration: '',
      permits: []
    });
    setShowServiceModal(true);
  };

  const handleServiceFormChange = (value, field) => {
    setServiceForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNextServiceStep = () => {
    if (serviceStep < 3) {
      setServiceStep(serviceStep + 1);
    }
  };

  const handlePrevServiceStep = () => {
    if (serviceStep > 1) {
      setServiceStep(serviceStep - 1);
    }
  };

  const handleSaveService = async () => {
    try {
      await addService(serviceForm);
      setShowServiceModal(false);
      setServiceStep(1);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du service:', error);
    }
  };

  // Rendu des onglets
  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return renderInformationTab();
      case 'document':
        return renderDocumentTab();
      case 'service':
        return renderServiceTab();
      case 'mission':
        return renderMissionTab();
      case 'paiement':
        return renderPaiementTab();
      case 'employe':
        return renderEmployeTab();
      case 'abonnement':
        return renderAbonnementTab();
      case 'evaluation':
        return renderEvaluationTab();
      default:
        return renderInformationTab();
    }
  };

  const renderInformationTab = () => (
    <div className="info-content">
      <div className="info-section">
        <h2>Section entreprise</h2>
        <div className="company-card">
          <div className="company-header">
            <div className="company-logo">
              {(() => {
                const profilePhoto = documents.find(doc => doc.document_type === 'profile_photo');
                return profilePhoto ? (
                  <img 
                    src={`${API_SERVER_URL}${profilePhoto.document_url}`} 
                    alt="Photo de profil"
                    className="avatar-image-small"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null;
              })()}
              <div className="avatar-placeholder" style={{display: documents.find(doc => doc.document_type === 'profile_photo') ? 'none' : 'flex'}}>
                {prestataire.first_name?.[0] || prestataire.business_name?.[0] || 'P'}
              </div>
            </div>
            <div className="company-info">
              <h3>{prestataire.name}</h3>
              <div className="contact-details">
                <p>{prestataire.email}</p>
                <p>{prestataire.phone}</p>
              </div>
              <div className="status-indicators">
                <span className="status-badge validated">✓ Document validé</span>
                <span className="status-badge active">Compte actif</span>
              </div>
            </div>
            <div className="company-contact">
              <p>{prestataire.phone}</p>
              <div className="service-tags">
                {(rawPrestataire?.services || rawPrestataire?.category_interests || []).slice(0, 4).map((service, idx) => (
                  <span key={idx} className="tag">
                    {typeof service === 'string' ? service : (service.name || service.service_name || 'Service')}
                  </span>
                ))}
                {(!rawPrestataire?.services?.length && !rawPrestataire?.category_interests?.length) && (
                  <span className="tag">Aucun service</span>
                )}
              </div>
            </div>
          </div>
          <div className="stats-bar">
            <span className="stat missions">{rawPrestataire?.statistics?.totalMissions || prestataire.missions || 0} Mission réalisées</span>
            <span className="stat reports">{rawPrestataire?.statistics?.totalReports || 0} Signalements reçus</span>
            <span className="stat commission">{(rawPrestataire?.statistics?.totalCommission || 0).toLocaleString('fr-FR')} CFA Commission cumulée</span>
          </div>
        </div>
      </div>

      <div className="info-section">
        <h2>Section gérant</h2>
        <div className="manager-card">
          <div className="manager-info">
            <div className="manager-avatar">
              {(() => {
                const managerPhoto = documents.find(doc => doc.document_type === 'manager_photo') || documents.find(doc => doc.document_type === 'profile_photo');
                return managerPhoto ? (
                  <img 
                    src={`${API_SERVER_URL}${managerPhoto.document_url}`} 
                    alt="Photo du dirigeant"
                    className="avatar-image-small"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null;
              })()}
              <div className="avatar-placeholder" style={{display: (documents.find(doc => doc.document_type === 'manager_photo') || documents.find(doc => doc.document_type === 'profile_photo')) ? 'none' : 'flex'}}>
                {prestataire.first_name?.[0] || prestataire.business_name?.[0] || 'P'}
              </div>
            </div>
            <div className="manager-details">
              <h3>{prestataire.name}</h3>
              <p>{prestataire.email}</p>
              <p>{prestataire.phone}</p>
              <p className="address">{prestataire.address}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Helper function to get document display name
  const getDocumentDisplayName = (documentType) => {
    const documentNames = {
      'profile_photo': 'Photo de profil',
      'national_id_front': 'CIN recto',
      'national_id_back': 'CIN verso',
      'rccm_document': 'RCCM',
      'ifu_document': 'IFU',
      'manager_photo': 'Photo du dirigeant'
    };
    return documentNames[documentType] || documentType;
  };

  // Helper function to get verification status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { label: 'En attente', className: 'btn-warning' },
      'verified': { label: 'Validé', className: 'btn-success' },
      'rejected': { label: 'Rejeté', className: 'btn-danger' }
    };
    const config = statusConfig[status] || statusConfig['pending'];
    return <button className={`btn ${config.className}`}>{config.label}</button>;
  };

  // Group documents by category
  const groupedDocuments = documents.reduce((acc, doc) => {
    if (doc.document_type.includes('national_id')) {
      if (!acc.identity) acc.identity = [];
      acc.identity.push(doc);
    } else if (doc.document_type === 'rccm_document') {
      if (!acc.rccm) acc.rccm = [];
      acc.rccm.push(doc);
    } else if (doc.document_type === 'ifu_document') {
      if (!acc.ifu) acc.ifu = [];
      acc.ifu.push(doc);
    } else if (doc.document_type === 'profile_photo' || doc.document_type === 'manager_photo') {
      if (!acc.photos) acc.photos = [];
      acc.photos.push(doc);
    }
    return acc;
  }, {});

  const renderDocumentTab = () => (
    <div className="tab-content">
      {documentsLoading ? (
        <div className="loading-state">Chargement des documents...</div>
      ) : documentsError ? (
        <div className="error-state">Erreur: {documentsError}</div>
      ) : (
        <div className="documents-section">
          {/* Photos de profil */}
          {groupedDocuments.photos && groupedDocuments.photos.length > 0 && (
            <div className="document-group">
              <h3>Photos</h3>
              <div className="document-images">
                {groupedDocuments.photos.map((doc) => (
                  <div key={doc.id} className="doc-image">
                    <img 
                      src={`${API_SERVER_URL}${doc.document_url}`} 
                      alt={getDocumentDisplayName(doc.document_type)}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="doc-placeholder" style={{display: 'none'}}>
                      <span>{getDocumentDisplayName(doc.document_type)}</span>
                    </div>
                    <div className="doc-info">
                      <span className="doc-name">{getDocumentDisplayName(doc.document_type)}</span>
                      {doc.verified_at && (
                        <span className="doc-verified-date">
                          Validé le {new Date(doc.verified_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                      {doc.verified_by && (
                        <span className="doc-verified-by">par {doc.verified_by}</span>
                      )}
                      {doc.rejection_reason && (
                        <span className="doc-rejection-reason">
                          Motif: {doc.rejection_reason}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {groupedDocuments.photos[0] && getStatusBadge(groupedDocuments.photos[0].verification_status)}
            </div>
          )}

          {/* Documents d'identité */}
          {groupedDocuments.identity && groupedDocuments.identity.length > 0 && (
            <div className="document-group">
              <h3>Carte d'Identité Nationale</h3>
              <div className="document-images">
                {groupedDocuments.identity.map((doc) => (
                  <div key={doc.id} className="doc-image">
                    <img 
                      src={`${API_SERVER_URL}${doc.document_url}`} 
                      alt={getDocumentDisplayName(doc.document_type)}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="doc-placeholder" style={{display: 'none'}}>
                      <span>{getDocumentDisplayName(doc.document_type)}</span>
                    </div>
                    <div className="doc-info">
                      <span className="doc-name">{getDocumentDisplayName(doc.document_type)}</span>
                      {doc.verified_at && (
                        <span className="doc-verified-date">
                          Validé le {new Date(doc.verified_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                      {doc.verified_by && (
                        <span className="doc-verified-by">par {doc.verified_by}</span>
                      )}
                      {doc.rejection_reason && (
                        <span className="doc-rejection-reason">
                          Motif: {doc.rejection_reason}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {groupedDocuments.identity[0] && getStatusBadge(groupedDocuments.identity[0].verification_status)}
            </div>
          )}

          {/* RCCM */}
          {groupedDocuments.rccm && groupedDocuments.rccm.length > 0 && (
            <div className="document-group">
              <h3>RCCM</h3>
              <div className="document-images">
                {groupedDocuments.rccm.map((doc) => (
                  <div key={doc.id} className="doc-image">
                    {doc.document_url.toLowerCase().includes('.pdf') ? (
                      <div className="pdf-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span>PDF</span>
                        <a href={`${API_SERVER_URL}${doc.document_url}`} target="_blank" rel="noopener noreferrer" className="pdf-link">
                          Voir le document
                        </a>
                      </div>
                    ) : (
                      <img 
                        src={`${API_SERVER_URL}${doc.document_url}`} 
                        alt={getDocumentDisplayName(doc.document_type)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    )}
                    <div className="doc-placeholder" style={{display: 'none'}}>
                      <span>{getDocumentDisplayName(doc.document_type)}</span>
                    </div>
                  </div>
                ))}
              </div>
              {groupedDocuments.rccm[0] && getStatusBadge(groupedDocuments.rccm[0].verification_status)}
            </div>
          )}

          {/* IFU */}
          {groupedDocuments.ifu && groupedDocuments.ifu.length > 0 && (
            <div className="document-group">
              <h3>IFU</h3>
              <div className="document-images">
                {groupedDocuments.ifu.map((doc) => (
                  <div key={doc.id} className="doc-image">
                    {doc.document_url.toLowerCase().includes('.pdf') ? (
                      <div className="pdf-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span>PDF</span>
                        <a href={`${API_SERVER_URL}${doc.document_url}`} target="_blank" rel="noopener noreferrer" className="pdf-link">
                          Voir le document
                        </a>
                      </div>
                    ) : (
                      <img 
                        src={`${API_SERVER_URL}${doc.document_url}`} 
                        alt={getDocumentDisplayName(doc.document_type)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    )}
                    <div className="doc-placeholder" style={{display: 'none'}}>
                      <span>{getDocumentDisplayName(doc.document_type)}</span>
                    </div>
                  </div>
                ))}
              </div>
              {groupedDocuments.ifu[0] && getStatusBadge(groupedDocuments.ifu[0].verification_status)}
            </div>
          )}

          {documents.length === 0 && (
            <div className="no-documents">
              <p>Aucun document disponible pour ce prestataire.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderServiceTab = () => (
    <div className="tab-content">
      <div className="services-header">
        <div className="search-filter">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input type="text" placeholder="Recherche de service" />
          </div>
          <select className="filter-select">
            <option>Filtre zone d'activité</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={handleAddService}>
          <FaPlus /> Ajout service
        </button>
      </div>

      <div className="services-grid">
        {servicesLoading ? (
          <div className="loading-state">Chargement des services...</div>
        ) : prestataire.services && prestataire.services.length > 0 ? (
          prestataire.services.map((service, index) => (
            <div key={service.id || index} className="service-card">
              <div className="service-image">
                <div className="service-placeholder" style={{
                  width: '100%',
                  height: '120px',
                  backgroundColor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <span style={{color: '#6b7280', fontSize: '14px'}}>
                    {service.name || 'Service'}
                  </span>
                </div>
              </div>
              <div className="service-info">
                <h4>{service.name || 'Service non défini'}</h4>
                <div className="service-toggle">
                  <label className="toggle">
                    <input 
                      type="checkbox" 
                      defaultChecked={service.is_available !== false} 
                      disabled
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="service-details">
                  <p><FaMapMarkerAlt /> Type de service</p>
                  <p>{service.description || service.name || 'Non défini'}</p>
                  {service.is_available !== undefined && (
                    <>
                      <p><FaRoute /> Disponibilité</p>
                      <p>{service.is_available ? 'Disponible' : 'Indisponible'}</p>
                    </>
                  )}
                </div>
                <div className="service-actions">
                  <button className="btn-delete">
                    <FaTrash />
                  </button>
                  <button className="btn btn-primary">Voir détail</button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state" style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6b7280'
          }}>
            <p>Aucun service configuré pour ce prestataire</p>
            <p style={{fontSize: '14px', marginTop: '8px'}}>
              Les services sélectionnés lors de l'inscription apparaîtront ici une fois configurés
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Helper pour obtenir la classe CSS du statut
  const getMissionStatusClass = (status) => {
    const statusClasses = {
      'pending': 'en-attente',
      'assigned': 'en-cours',
      'in_progress': 'en-cours',
      'completed': 'terminee',
      'cancelled': 'annulee',
      'disputed': 'litige'
    };
    return statusClasses[status] || 'en-attente';
  };

  // Helper pour obtenir le label du statut
  const getMissionStatusLabel = (status) => {
    const statusLabels = {
      'pending': 'En attente',
      'assigned': 'En cours',
      'in_progress': 'En cours',
      'completed': 'Terminée',
      'cancelled': 'Annulée',
      'disputed': 'Litige'
    };
    return statusLabels[status] || status;
  };

  const renderMissionTab = () => (
    <div className="tab-content">
      {missionsLoading ? (
        <div className="loading-state">Chargement des missions...</div>
      ) : missions.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
          <p>Aucune mission pour ce prestataire</p>
        </div>
      ) : (
        <div className="missions-grid">
          {missions.map((mission) => (
            <div key={mission.id} className="mission-card">
              <div className="mission-header">
                <h4># Mission {mission.id?.substring(0, 8) || mission.mission_id}</h4>
                <span className={`mission-status ${getMissionStatusClass(mission.status)}`}>
                  {getMissionStatusLabel(mission.status)}
                </span>
                <input type="checkbox" />
              </div>

              <div className="mission-tags">
                {mission.service_type && <span className="tag">{mission.service_type}</span>}
                {mission.vehicle_type && <span className="tag">{mission.vehicle_type}</span>}
              </div>

              <div className="mission-dates">
                <p>📅 Création : {mission.created_at ? new Date(mission.created_at).toLocaleDateString('fr-FR') : 'N/A'}</p>
                <p>📅 Exécution : {mission.scheduled_date ? new Date(mission.scheduled_date).toLocaleDateString('fr-FR') : (mission.execution_date ? new Date(mission.execution_date).toLocaleDateString('fr-FR') : 'N/A')}</p>
              </div>

              {mission.rating && (
                <div className="mission-rating">
                  <div className="stars">
                    {[1,2,3,4,5].map(star => (
                      <span key={star} className={`star ${star <= mission.rating ? 'filled' : ''}`}>⭐</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mission-actions">
                {mission.status !== 'cancelled' && mission.status !== 'completed' && (
                  <button className="btn-cancel">Annuler mission</button>
                )}
                {mission.status === 'completed' && (
                  <button className="btn-refund">Rembourser</button>
                )}
                <button className="btn btn-primary" onClick={() => navigate(`/missions/${mission.id}`)}>Voir détail</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Helper pour le statut de paiement
  const getPaymentStatusClass = (status) => {
    const classes = {
      'paid': 'paid',
      'completed': 'paid',
      'pending': 'pending',
      'failed': 'failed',
      'refunded': 'refunded'
    };
    return classes[status] || 'pending';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      'paid': '✓ Payé',
      'completed': '✓ Payé',
      'pending': '⏳ En attente',
      'failed': '✗ Échec',
      'refunded': '↩ Remboursé'
    };
    return labels[status] || status;
  };

  const renderPaiementTab = () => (
    <div className="tab-content">
      <div className="payment-header">
        <div className="date-filter">
          <input type="date" placeholder="jj/mm/aaaa" />
        </div>
        <div className="payment-status-filters">
          <button className="filter-btn active">Tous</button>
          <button className="filter-btn">Payés</button>
          <button className="filter-btn">En attente</button>
          <button className="filter-btn">Échec</button>
        </div>
        <button className="btn">Export CSV</button>
      </div>

      <div className="payment-stats">
        <div className="stat-card">
          <h4>Solde disponible</h4>
          <p className="amount">{paiementStats.solde.toLocaleString('fr-FR')} CFA</p>
        </div>
        <div className="stat-card">
          <h4>Revenus totaux</h4>
          <p className="amount">{paiementStats.revenus.toLocaleString('fr-FR')} CFA</p>
        </div>
        <div className="stat-card">
          <h4>Commissions prélevées</h4>
          <p className="amount">{paiementStats.commissions.toLocaleString('fr-FR')} CFA</p>
        </div>
      </div>

      {paiementsLoading ? (
        <div className="loading-state">Chargement des paiements...</div>
      ) : paiements.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
          <p>Aucun paiement enregistré</p>
        </div>
      ) : (
        <div className="payments-list">
          {paiements.map((paiement) => (
            <div key={paiement.id} className="payment-card">
              <div className="payment-date">
                <span>📅 {paiement.created_at ? new Date(paiement.created_at).toLocaleDateString('fr-FR') : 'N/A'}</span>
                <input type="checkbox" />
              </div>

              <div className="payment-details">
                <div className="payment-amounts">
                  <p>Montant brut: <span className="amount">{parseFloat(paiement.amount || 0).toLocaleString('fr-FR')} CFA</span></p>
                  <p>Commission prélevée: <span className="amount">{parseFloat(paiement.commission || 0).toLocaleString('fr-FR')} CFA</span></p>
                  <p>Montant net payé: <span className="amount">{(parseFloat(paiement.amount || 0) - parseFloat(paiement.commission || 0)).toLocaleString('fr-FR')} CFA</span></p>
                </div>

                <div className="payment-status">
                  <span className={`status ${getPaymentStatusClass(paiement.status)}`}>
                    {getPaymentStatusLabel(paiement.status)}
                  </span>
                </div>

                <div className="payment-info">
                  <p>Mission #{paiement.mission_id?.substring(0, 8) || 'N/A'}</p>
                  <p>REF {paiement.reference || paiement.id?.substring(0, 6)}</p>
                  <p>{paiement.payment_method || 'Mobile Money'}</p>
                </div>
              </div>

              <div className="payment-actions">
                <button className="btn">📄 Télécharger reçu PDF</button>
                <button className="btn btn-primary" onClick={() => navigate(`/paiements/${paiement.id}`)}>Détails</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderEmployeTab = () => (
    <div className="tab-content">
      <div className="employees-header">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input type="text" placeholder="Recherche de service" />
        </div>
        <button className="btn btn-primary" onClick={handleAddEmployee}>
          <FaPlus /> Ajout employé
        </button>
      </div>

      <div className="employees-table">
        <table>
          <thead>
            <tr>
              <th><input type="checkbox" /></th>
              <th>Nom et prénom <FaSort /></th>
              <th>Email <FaSort /></th>
              <th>Téléphone <FaSort /></th>
              <th>Rôle <FaSort /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {employees?.map((employee, index) => (
              <tr key={employee.id || index}>
                <td><input type="checkbox" /></td>
                <td>{`${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'N/A'}</td>
                <td>{employee.email || 'N/A'}</td>
                <td>{employee.phone || 'N/A'}</td>
                <td>{employee.role || 'Non défini'}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEditEmployee(employee)}
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeleteEmployee(employee.id)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Helper pour formater la durée de l'abonnement
  const formatDuration = (duration) => {
    const durations = {
      'monthly': 'Mensuelle',
      'quarterly': 'Trimestrielle',
      'yearly': 'Annuelle',
      'annual': 'Annuelle'
    };
    return durations[duration] || duration || 'Mensuelle';
  };

  const renderAbonnementTab = () => (
    <div className="tab-content">
      {abonnementLoading ? (
        <div className="loading-state">Chargement des abonnements...</div>
      ) : plans.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
          <p>Aucun plan d'abonnement disponible</p>
        </div>
      ) : (
        <div className="subscription-plans">
          {plans.map((plan) => {
            const isCurrentPlan = currentAbonnement && currentAbonnement.plan_id === plan.id;
            return (
              <div key={plan.id} className={`plan-card ${isCurrentPlan ? 'active' : ''}`}>
                <div className="plan-toggle">
                  <label className="toggle">
                    <input type="checkbox" defaultChecked={isCurrentPlan} disabled={!isCurrentPlan} />
                    <span className="slider"></span>
                  </label>
                </div>
                <h3>{plan.name || plan.plan_name}</h3>
                <p className="plan-price">{parseFloat(plan.price || 0).toLocaleString('fr-FR')} FCFA</p>
                <p className="plan-duration">Durée : {formatDuration(plan.duration || plan.billing_cycle)}</p>

                <div className="plan-features">
                  {plan.features && (
                    <>
                      <h4>Services inclus :</h4>
                      <ul>
                        {(Array.isArray(plan.features) ? plan.features : (plan.features.includes || [])).map((feature, idx) => (
                          <li key={idx}>✓ {feature}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {plan.limits && (
                    <>
                      <h4>Limites :</h4>
                      <ul>
                        {plan.limits.max_services && <li>✓ {plan.limits.max_services} service(s) actif(s)</li>}
                        {plan.limits.max_vehicles && <li>✓ {plan.limits.max_vehicles} véhicule(s) déclaré(s)</li>}
                        {plan.limits.max_missions && <li>✓ {plan.limits.max_missions} missions maximum</li>}
                      </ul>
                    </>
                  )}

                  {plan.description && !plan.features && (
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>{plan.description}</p>
                  )}
                </div>

                {isCurrentPlan && (
                  <button className="btn btn-primary">Modifier</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderEvaluationTab = () => (
    <div className="tab-content">
      <div className="evaluation-header">
        <h3>Evaluation par les clients</h3>
        <div className="overall-rating">
          {[1,2,3,4,5].map(star => (
            <span key={star} className={`star ${star <= Math.round(averageRating) ? 'filled' : ''}`}>⭐</span>
          ))}
          <span style={{ marginLeft: '8px', color: '#6b7280' }}>({averageRating.toFixed(1)}/5)</span>
        </div>
      </div>

      {evaluationsLoading ? (
        <div className="loading-state">Chargement des évaluations...</div>
      ) : evaluations.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
          <p>Aucune évaluation pour ce prestataire</p>
        </div>
      ) : (
        <div className="evaluations-grid">
          {evaluations.map((evaluation, index) => (
            <div key={evaluation.id || index} className="evaluation-card">
              <div className="evaluation-header">
                <div className="user-info">
                  <div className="avatar-circle">
                    {(evaluation.client_name || evaluation.user_name || 'C').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4>{evaluation.client_name || evaluation.user_name || 'Client anonyme'}</h4>
                    <p>{evaluation.client_type || evaluation.user_type || 'Client'}</p>
                  </div>
                </div>
                <span className="evaluation-date">
                  {evaluation.created_at ? new Date(evaluation.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                </span>
              </div>

              <div className="rating">
                {[1,2,3,4,5].map(star => (
                  <span
                    key={star}
                    className={`star ${star <= (evaluation.rating || 0) ? 'filled' : ''}`}
                  >
                    ⭐
                  </span>
                ))}
              </div>

              <p className="evaluation-text">
                {evaluation.comment || evaluation.review || evaluation.message || 'Aucun commentaire'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="prestataire-detail">
      {/* Header */}
      <div className="prestataire-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/prestataires')}>
            <FaArrowLeft />
            Fiche détails prestataires
          </button>
        </div>
        <div className="header-right">
          <button className="btn-secondary">Suspendre</button>
          <button className="btn-danger">Supprimer</button>
          <button className="btn-success">Valider</button>
          <button 
            className="btn-primary"
            onClick={() => setShowMessageModal(true)}
          >
            Message direct
          </button>
        </div>
      </div>

      {/* Prestataire Banner */}
      <div className="prestataire-banner">
        <div className="banner-left">
          <div className="prestataire-avatar-large">
            {(() => {
              const profilePhoto = documents.find(doc => doc.document_type === 'profile_photo');
              return profilePhoto ? (
                <img 
                  src={`${API_SERVER_URL}${profilePhoto.document_url}`} 
                  alt="Photo de profil"
                  className="avatar-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null;
            })()}
            <div className="avatar-placeholder" style={{display: documents.find(doc => doc.document_type === 'profile_photo') ? 'none' : 'flex'}}>
              {prestataire.first_name?.[0] || prestataire.business_name?.[0] || 'P'}
            </div>
          </div>
          <div className="prestataire-main-info">
            <h1>{prestataire.name}</h1>
            <p className="prestataire-id">{prestataire.prestataireId}</p>
          </div>
        </div>
        <div className="banner-right">
          <div className="service-tags">
            {(rawPrestataire?.services || rawPrestataire?.category_interests || []).slice(0, 4).map((service, idx) => (
              <span key={idx} className="service-tag">
                {typeof service === 'string' ? service : (service.name || service.service_name || 'Service')}
              </span>
            ))}
            {(!rawPrestataire?.services?.length && !rawPrestataire?.category_interests?.length) && (
              <span className="service-tag">Aucun service configuré</span>
            )}
          </div>
          <div className={`status-badge ${rawPrestataire?.verification_status === 'verified' ? 'validated' : 'pending'}`}>
            {rawPrestataire?.verification_status === 'verified' ? '✓ Document validé' : '⏳ En attente de validation'}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="detail-layout">
        {/* Sidebar Navigation */}
        <div className="detail-sidebar">
          {[
            { key: 'information', label: 'Information générale', icon: <FaInfoCircle /> },
            { key: 'document', label: 'Document', icon: <FaFileAlt /> },
            { key: 'abonnement', label: 'Abonnement', icon: <FaCreditCard /> },
            { key: 'evaluation', label: 'Evaluation', icon: <FaStar /> },
            { key: 'service', label: 'Service', icon: <FaCogs /> },
            { key: 'mission', label: 'Mission', icon: <FaTasks /> },
            { key: 'paiement', label: 'Paiement', icon: <FaMoneyBillWave /> },
            { key: 'employe', label: 'Employé', icon: <FaBriefcase /> }
          ].map((tab) => (
            <button
              key={tab.key}
              className={`sidebar-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="detail-main">
          {renderTabContent()}
        </div>
      </div>

      {/* Modales */}
      
      {/* Modale Ajout Employé */}
      {showEmployeeModal && (
        <Modal
          title={editingEmployee ? 'Modifier employé' : 'Ajouter employé'}
          onClose={() => setShowEmployeeModal(false)}
        >
          <div className="modal-form">
            <FormField
              label="Nom"
              type="text"
              value={employeeForm.firstName}
              onChange={(value) => handleEmployeeFormChange(value, 'firstName')}
              placeholder="example@email.com"
            />
            
            <FormField
              label="Prénom"
              type="text"
              value={employeeForm.lastName}
              onChange={(value) => handleEmployeeFormChange(value, 'lastName')}
              placeholder="example@email.com"
            />
            
            <FormField
              label="Téléphone"
              type="tel"
              value={employeeForm.phone}
              onChange={(value) => handleEmployeeFormChange(value, 'phone')}
              placeholder="example@email.com"
            />
            
            <FormField
              label="Rôle"
              type="select"
              value={employeeForm.role}
              onChange={(value) => handleEmployeeFormChange(value, 'role')}
              options={[
                { value: '', label: 'Sélectionner un rôle' },
                { value: 'Conducteur', label: 'Conducteur' },
                { value: 'Gestionnaire', label: 'Gestionnaire' },
                { value: 'Administrateur', label: 'Administrateur' },
                { value: 'Technicien', label: 'Technicien' },
                { value: 'Livreur', label: 'Livreur' }
              ]}
            />
            
            <div className="modal-actions">
              <button 
                className="btn secondary"
                onClick={() => setShowEmployeeModal(false)}
              >
                Annuler
              </button>
              <button 
                className="btn primary"
                onClick={handleSaveEmployee}
              >
                Ajouter
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modale Message */}
      {showMessageModal && (
        <Modal
          title="Message"
          onClose={() => setShowMessageModal(false)}
        >
          <div className="modal-form">
            <FormField
              label="Message direct"
              type="textarea"
              value=""
              onChange={() => {}}
              placeholder="Tapez ici..."
            />
            
            <div className="modal-actions">
              <button 
                className="btn secondary"
                onClick={() => setShowMessageModal(false)}
              >
                Annuler
              </button>
              <button className="btn primary">
                Envoyer
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modale Ajout Service (Multi-étapes) */}
      {showServiceModal && (
        <Modal
          title="Ajout service"
          onClose={() => setShowServiceModal(false)}
          subtitle={`Information sur le ${serviceStep === 1 ? 'service' : serviceStep === 2 ? 'véhicule' : 'conducteur'}`}
        >
          <div className="modal-form">
            <div className="step-indicators">
              <div className={`step ${serviceStep >= 1 ? 'active' : ''}`}>●</div>
              <div className={`step ${serviceStep >= 2 ? 'active' : ''}`}>●</div>
              <div className={`step ${serviceStep >= 3 ? 'active' : ''}`}>●</div>
            </div>

            {serviceStep === 1 && (
              <>
                <button className="upload-btn">
                  <FaPlus /> Importer une image de service
                </button>
                
                <FormField
                  label="Nom du service"
                  type="text"
                  value={serviceForm.name}
                  onChange={(value) => handleServiceFormChange(value, 'name')}
                />
                
                <FormField
                  label="Type de service"
                  type="select"
                  value={serviceForm.type}
                  onChange={(value) => handleServiceFormChange(value, 'type')}
                  options={[
                    { value: '', label: serviceCategories && serviceCategories.length > 0 ? 'Sélectionner un type' : 'Chargement...' },
                    ...(serviceCategories && serviceCategories.length > 0
                      ? serviceCategories.map(cat => ({
                          value: cat.id || cat.name || cat,
                          label: cat.name || cat
                        }))
                      : []
                    )
                  ]}
                />
                
                <div className="form-row">
                  <FormField
                    label="Type de tarification"
                    type="select"
                    value={serviceForm.pricing}
                    onChange={(value) => handleServiceFormChange(value, 'pricing')}
                    options={[
                      { value: 'Prix fixe', label: 'Prix fixe' }
                    ]}
                  />
                  
                  <FormField
                    label="Rayon de couverture"
                    type="text"
                    value={serviceForm.coverage}
                    onChange={(value) => handleServiceFormChange(value, 'coverage')}
                  />
                </div>
                
                <FormField
                  label="Zone d'activité"
                  type="text"
                  value={serviceForm.zone || ''}
                  onChange={(value) => handleServiceFormChange(value, 'zone')}
                  placeholder="Sélectionnez ou saisissez une zone"
                />

                <div className="zone-buttons">
                  {availableZones && availableZones.length > 0 ? (
                    availableZones.slice(0, 6).map((zone) => (
                      <button
                        key={zone.id}
                        type="button"
                        className={`zone-btn ${serviceForm.selectedZones?.includes(zone.id) ? 'active' : ''}`}
                        onClick={() => {
                          const currentZones = serviceForm.selectedZones || [];
                          const newZones = currentZones.includes(zone.id)
                            ? currentZones.filter(z => z !== zone.id)
                            : [...currentZones, zone.id];
                          handleServiceFormChange(newZones, 'selectedZones');
                        }}
                      >
                        {zone.name}
                      </button>
                    ))
                  ) : (
                    <span className="no-zones">Chargement des zones...</span>
                  )}
                </div>
                
                <button className="btn primary" onClick={handleNextServiceStep}>
                  Ajouter
                </button>
              </>
            )}

            {serviceStep === 2 && (
              <>
                <button className="upload-btn">
                  <FaPlus /> Importer papier du voiture
                </button>
                
                <FormField
                  label="Type de voiture"
                  type="text"
                  value={serviceForm.vehicleType}
                  onChange={(value) => handleServiceFormChange(value, 'vehicleType')}
                />
                
                <FormField
                  label="Nombre de place"
                  type="text"
                  value={serviceForm.seats}
                  onChange={(value) => handleServiceFormChange(value, 'seats')}
                />
                
                <FormField
                  label="Immatriculation"
                  type="text"
                  value={serviceForm.registration}
                  onChange={(value) => handleServiceFormChange(value, 'registration')}
                />
                
                <div className="modal-actions">
                  <button className="btn secondary" onClick={handlePrevServiceStep}>
                    Précédent
                  </button>
                  <button className="btn primary" onClick={handleNextServiceStep}>
                    Suivant
                  </button>
                </div>
              </>
            )}

            {serviceStep === 3 && (
              <>
                <button className="upload-btn">
                  <FaPlus /> Importer photo du conducteur
                </button>
                
                <div className="form-row">
                  <FormField
                    label="Nom"
                    type="text"
                    value={serviceForm.driverName}
                    onChange={(value) => handleServiceFormChange(value, 'driverName')}
                  />
                  
                  <FormField
                    label="Prénom"
                    type="text"
                    value={serviceForm.driverFirstName}
                    onChange={(value) => handleServiceFormChange(value, 'driverFirstName')}
                  />
                </div>
                
                <div className="form-row">
                  <FormField
                    label="Sexe"
                    type="select"
                    value={serviceForm.driverGender}
                    onChange={(value) => handleServiceFormChange(value, 'driverGender')}
                    options={[
                      { value: '', label: 'Sélectionner le sexe' },
                      { value: 'M', label: 'Masculin' },
                      { value: 'F', label: 'Féminin' }
                    ]}
                  />
                  
                  <FormField
                    label="Immatriculation"
                    type="text"
                    value={serviceForm.driverRegistration}
                    onChange={(value) => handleServiceFormChange(value, 'driverRegistration')}
                  />
                </div>
                
                <div className="permits-section">
                  <h4>Permis</h4>
                  <div className="permits-grid">
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(permit => (
                      <label key={permit} className="permit-checkbox">
                        <input type="checkbox" />
                        <span>Permis {permit}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button className="btn secondary" onClick={handlePrevServiceStep}>
                    Précédent
                  </button>
                  <button className="btn primary" onClick={handleSaveService}>
                    Enregistrer et terminer
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Confirm Modal */}
      {confirmModal.isOpen && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}
    </div>
  );
};

export default PrestataireDetail;